/* =========================================================
   CAREER LAUNCH SECTION
========================================================= */
import {
  IconArrowUpRight,
  IconCode,
  IconSpeakerphone,
  IconCheck,
  IconBriefcase,
  IconCertificate,
} from "@tabler/icons-react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
function CareerLaunchSection() {
  const opportunities = [
    {
      number: "01",
      icon: IconBriefcase,
      eyebrow: "Practical Experience",
      title: "Internship with Certificate",
      description:
        "Gain practical exposure through structured IT internship opportunities designed to help students understand real development workflows, teamwork and project execution.",
      tag: "Internship Program",
    },

    {
      number: "02",
      icon: IconSpeakerphone,
      eyebrow: "Learning Beyond Classrooms",
      title: "Technical Bootcamps",
      description:
        "Participate in focused bootcamps, workshops and intensive learning sessions covering modern technologies, development practices and career-oriented technical skills.",
      tag: "Hands-on Bootcamps",
    },

    {
      number: "03",
      icon: IconCode,
      eyebrow: "Academic Project Support",
      title: "Final Year IT Projects",
      description:
        "Get guidance for final-year IT projects with support in project planning, development, implementation, documentation and practical presentation.",
      tag: "Project Guidance",
    },

    {
      number: "04",
      icon: IconCertificate,
      eyebrow: "Course Recognition",
      title: "Course Completion Certificate",
      description:
        "Students completing eligible WE3X training programs receive a course completion certificate, including Startup India affiliation where applicable to the program.",
      tag: "Certification",
    },
  ];


  return (
    <section
      className="
        relative
        overflow-hidden
        border-y
        border-neutral-200
        bg-white
        py-20
        sm:py-24
        lg:py-28
      "
    >

      {/* =====================================================
          BACKGROUND DETAIL
      ===================================================== */}

      <div
        className="
          pointer-events-none
          absolute
          -left-32
          top-1/2
          h-[330px]
          w-[330px]
          -translate-y-1/2
          rounded-full
          bg-blue-100/60
          blur-[120px]
        "
      />


      <div
        className="
          pointer-events-none
          absolute
          -right-32
          bottom-0
          h-[300px]
          w-[300px]
          rounded-full
          bg-blue-50
          blur-[100px]
        "
      />


      <div
        className="
          mx-auto
          max-w-7xl
          px-5
          sm:px-8
          lg:px-10
        "
      >

        {/* =====================================================
            HEADER
        ===================================================== */}

        <div
          className="
            grid
            gap-10
            lg:grid-cols-[0.9fr_1.1fr]
            lg:items-end
            lg:gap-20
          "
        >

          {/* LEFT */}

          <motion.div
            variants={fadeUp}
            initial="hidden"
            whileInView="visible"
            viewport={{
              once: true,
              amount: 0.3,
            }}
          >

            <div
              className="
                mb-5
                flex
                items-center
                gap-3
                text-[11px]
                font-black
                uppercase
                tracking-[0.2em]
                text-[#55aef3]
              "
            >
              <span
                className="
                  h-[1px]
                  w-9
                  bg-blue-600
                "
              />

              Beyond the Course
            </div>


            <h2
              className="
                merriweather
                max-w-xl
                text-3xl
                font-black
                tracking-[-0.04em]
                text-neutral-950
                sm:text-4xl
                lg:text-[48px]
                lg:leading-[1.08]
              "
            >
              Learn the skill.

              <span
                className="
                  block
                  text-[#55aef3]
                "
              >
                Experience the industry.
              </span>
            </h2>

          </motion.div>


          {/* RIGHT */}

          <motion.div
            variants={fadeUp}
            initial="hidden"
            whileInView="visible"
            viewport={{
              once: true,
              amount: 0.3,
            }}
            className="
              max-w-2xl
              lg:pb-1
            "
          >

            <p
              className="
                text-[15px]
                leading-7
                text-neutral-600
                sm:text-[16px]
              "
            >
              WE3X Institute goes beyond classroom learning by combining
              practical IT training with internships, technical bootcamps,
              final-year project guidance and certification opportunities
              for students and graduates.
            </p>


            <p
              className="
                mt-4
                text-[14px]
                leading-7
                text-neutral-500
              "
            >
              Our learning approach is designed for students looking for
              practical, job-oriented IT training in Karaikudi and the
              confidence to apply their skills in real-world environments.
            </p>

          </motion.div>

        </div>



        {/* =====================================================
            JOURNEY
        ===================================================== */}

        <motion.div
          variants={staggerContainer}
          initial="hidden"
          whileInView="visible"
          viewport={{
            once: true,
            amount: 0.1,
          }}
          className="
            relative
            mt-14
            grid
            gap-4
            sm:mt-16
            md:grid-cols-2
            lg:grid-cols-4
          "
        >

          {/* DESKTOP CONNECTING LINE */}

          <div
            className="
              pointer-events-none
              absolute
              left-[8%]
              right-[8%]
              top-[34px]
              hidden
              h-[1px]
              overflow-hidden
              bg-neutral-200
              lg:block
            "
          >

            <motion.div
              initial={{
                x: "-100%",
              }}
              whileInView={{
                x: "100%",
              }}
              viewport={{
                once: false,
              }}
              transition={{
                duration: 3.5,
                repeat: Infinity,
                repeatDelay: 1,
                ease: "easeInOut",
              }}
              className="
                h-full
                w-[35%]
                bg-gradient-to-r
                from-transparent
                via-blue-500
                to-transparent
              "
            />

          </div>


          {opportunities.map(
            (
              item,
              index
            ) => {

              const ItemIcon =
                item.icon;


              return (
                <motion.article
                  key={
                    item.number
                  }
                  variants={cardAnimation}
                  whileHover={{
                    y: -5,
                  }}
                  className="
                    group
                    relative
                    z-10
                    overflow-hidden
                    rounded-[22px]
                    border
                    border-neutral-200
                    bg-white
                    px-5
                    pb-6
                    pt-5
                    transition-all
                    duration-300
                    hover:border-blue-200
                    hover:shadow-[0_20px_50px_rgba(0,0,0,0.06)]
                    sm:p-6
                  "
                >

                  {/* TOP */}

                  <div
                    className="
                      flex
                      items-center
                      justify-between
                      gap-4
                    "
                  >

                    {/* ICON */}

                    <motion.div
                      whileHover={{
                        rotate: 5,
                        scale: 1.08,
                      }}
                      className="
                        flex
                        h-11
                        w-11
                        items-center
                        justify-center
                        rounded-[13px]
                        border
                        border-blue-100
                        bg-blue-50
                        text-[#55aef3]
                        transition-colors
                        duration-300
                        group-hover:bg-blue-600
                        group-hover:text-white
                      "
                    >

                      <ItemIcon
                        size={20}
                        stroke={1.8}
                      />

                    </motion.div>


                    {/* NUMBER */}

                    <span
                      className="
                        merriweather
                        text-[12px]
                        font-black
                        text-neutral-300
                      "
                    >
                      {item.number}
                    </span>

                  </div>


                  {/* CATEGORY */}

                  <p
                    className="
                      mt-7
                      text-[9px]
                      font-black
                      uppercase
                      tracking-[0.17em]
                      text-[#55aef3]
                    "
                  >
                    {item.eyebrow}
                  </p>


                  {/* TITLE */}

                  <h3
                    className="
                      merriweather
                      mt-2
                      text-[20px]
                      font-black
                      leading-[1.25]
                      tracking-[-0.025em]
                      text-neutral-950
                    "
                  >
                    {item.title}
                  </h3>


                  {/* DESCRIPTION */}

                  <p
                    className="
                      mt-4
                      text-[13px]
                      leading-6
                      text-neutral-500
                    "
                  >
                    {item.description}
                  </p>


                  {/* TAG */}

                  <div
                    className="
                      mt-6
                      flex
                      items-center
                      gap-2
                    "
                  >

                    <span
                      className="
                        relative
                        flex
                        h-2
                        w-2
                      "
                    >

                      <motion.span
                        animate={{
                          scale: [
                            1,
                            1.8,
                            1,
                          ],

                          opacity: [
                            0.7,
                            0,
                            0.7,
                          ],
                        }}
                        transition={{
                          duration:
                            2 +
                            index *
                              0.2,

                          repeat:
                            Infinity,
                        }}
                        className="
                          absolute
                          inset-0
                          rounded-full
                          bg-blue-400
                        "
                      />


                      <span
                        className="
                          relative
                          h-2
                          w-2
                          rounded-full
                          bg-blue-600
                        "
                      />

                    </span>


                    <span
                      className="
                        text-[9px]
                        font-bold
                        uppercase
                        tracking-[0.13em]
                        text-neutral-400
                      "
                    >
                      {item.tag}
                    </span>

                  </div>


                  {/* HOVER LINE */}

                  <span
                    className="
                      absolute
                      bottom-0
                      left-0
                      h-[2px]
                      w-0
                      bg-blue-600
                      transition-all
                      duration-500
                      group-hover:w-full
                    "
                  />

                </motion.article>
              );

            }
          )}

        </motion.div>



        {/* =====================================================
            BOTTOM CTA
        ===================================================== */}

        <motion.div
          variants={fadeUp}
          initial="hidden"
          whileInView="visible"
          viewport={{
            once: true,
            amount: 0.3,
          }}
          className="
            mt-10
            flex
            flex-col
            gap-5
            rounded-[20px]
            border
            border-neutral-200
            bg-neutral-50
            px-5
            py-5
            sm:flex-row
            sm:items-center
            sm:justify-between
            sm:px-7
          "
        >

          <div
            className="
              flex
              items-start
              gap-3
            "
          >

            <div
              className="
                mt-0.5
                flex
                h-9
                w-9
                shrink-0
                items-center
                justify-center
                rounded-full
                bg-neutral-950
                text-white
              "
            >
              <IconCheck
                size={16}
              />
            </div>


            <div>

              <p
                className="
                  text-sm
                  font-black
                  text-neutral-950
                "
              >
                Build more than a certificate.
              </p>


              <p
                className="
                  mt-1
                  text-xs
                  leading-5
                  text-neutral-500
                "
              >
                Build practical skills, project experience and career-ready
                confidence with WE3X Institute.
              </p>

            </div>

          </div>


          <Link
            to="/institute"
            className="
              group
              inline-flex
              shrink-0
              items-center
              gap-2
              text-xs
              font-black
              text-neutral-950
            "
          >
            Explore IT Training & Internship Opportunities

            <IconArrowUpRight
              size={15}
              className="
                text-[#55aef3]
                transition-transform
                duration-300
                group-hover:translate-x-1
                group-hover:-translate-y-1
              "
            />

          </Link>

        </motion.div>

      </div>
    </section>
  );
}
const fadeUp = {
  hidden: {
    opacity: 0,
    y: 40,
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.7,
      ease: [0.22, 1, 0.36, 1],
    },
  },
};

const fadeLeft = {
  hidden: {
    opacity: 0,
    x: -60,
  },
  visible: {
    opacity: 1,
    x: 0,
    transition: {
      duration: 0.8,
      ease: [0.22, 1, 0.36, 1],
    },
  },
};

const fadeRight = {
  hidden: {
    opacity: 0,
    x: 60,
  },
  visible: {
    opacity: 1,
    x: 0,
    transition: {
      duration: 0.8,
      ease: [0.22, 1, 0.36, 1],
    },
  },
};

const staggerContainer = {
  hidden: {},
  visible: {
    transition: {
      staggerChildren: 0.12,
    },
  },
};

const cardAnimation = {
  hidden: {
    opacity: 0,
    y: 50,
    scale: 0.96,
  },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: {
      duration: 0.65,
      ease: [0.22, 1, 0.36, 1],
    },
  },
};

const statAnimation = {
  hidden: {
    opacity: 0,
    y: 30,
    scale: 0.9,
  },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: {
      duration: 0.6,
      ease: [0.22, 1, 0.36, 1],
    },
  },
};

export default CareerLaunchSection;